import { Component, inject, OnInit } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ProviderDataReviewListComponent } from 'app/capture-admin/components/provider-data-review-list/provider-data-review-list.component';
import { UserSettings } from 'app/core/enums/user-settings.enum';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { ProviderDataReview } from 'app/core/models/provider-data-review.model';
import { MyProviderDataReviewQueueSettings } from 'app/core/models/user-settings/provider-data-review-settings.model';
import { NavigationService } from 'app/core/services/navigation.service';
import { PagedProviderDataReviews, ProviderDataReviewsService } from 'app/core/services/provider-data-reviews.service';
import { UserSettingsService } from 'app/core/services/user-settings.service';
import { PaginationHeaderComponent } from 'app/shared/components/pagination-header/pagination-header.component';
import { PaginationComponent } from 'app/shared/components/pagination/pagination.component';
import { remove, isEmpty } from 'lodash-es';

@Component({
    selector: 'app-capture-admin-my-provider-data-reviews',
    templateUrl: './capture-admin-my-provider-data-reviews.component.html',
    styleUrls: ['./capture-admin-my-provider-data-reviews.component.scss'],
    imports: [
      NgSelectComponent,
      PaginationHeaderComponent,
      PaginationComponent,
      MatProgressSpinner,
      ProviderDataReviewListComponent
    ]
})
export class CaptureAdminMyProviderDataReviewsComponent implements OnInit {
  private providerDataReviewsService = inject(ProviderDataReviewsService);
  private navigationService = inject(NavigationService);
  private userSettingsService = inject(UserSettingsService);

  dataReviews: ProviderDataReview[] = [];
  selectedDataReviewIds: number[] = [];

  loading = false;
  paging = LimitOffsetPaging.empty;

  ngOnInit() {
    this.loadQueueSettings();
    this.loadReviews();
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadReviews();
    this.saveQueueSettings();
  }

  onPageChange() {
    this.loadReviews();
    this.saveQueueSettings();
  }

  onProviderDataReviewSelected(providerDataReview: ProviderDataReview) {
    this.selectedDataReviewIds.push(providerDataReview.id);
  }

  onProviderDataReviewDeselected(providerDataReview: ProviderDataReview) {
    remove(this.selectedDataReviewIds, id => id === providerDataReview.id);
  }

  onAssignmentUpdate(userId) {
    this.providerDataReviewsService.batchAssignedTo(this.selectedDataReviewIds, userId).subscribe(() => {
      this.navigationService.reloadCurrentUrl();
    });
  }

  onAssignmentClearSelection() {
    this.selectedDataReviewIds = [];
  }

  get showPaginationHeader() {
    return isEmpty(this.selectedDataReviewIds);
  }

  private loadReviews(showLoading = true) {
    if (showLoading) {
      this.loading = true;
    }

    this.providerDataReviewsService
      .getMyQueue(this.paging.currentPage, this.paging.pageSize)
      .subscribe((result: PagedProviderDataReviews) => {
        this.dataReviews = result.records || [];
        this.paging = result.meta.paging;
        this.loading = false;
      });
  }

  private loadQueueSettings() {
    const queueSettings: MyProviderDataReviewQueueSettings =
      this.userSettingsService.get<MyProviderDataReviewQueueSettings>(
        UserSettings.captureAdminMyProviderDataReviewQueueSettings
      );

    if (queueSettings) {
      this.paging = queueSettings.paging;
    }
  }

  private saveQueueSettings() {
    const queueSettings = new MyProviderDataReviewQueueSettings();
    queueSettings.paging = this.paging;

    this.userSettingsService.
      save<MyProviderDataReviewQueueSettings>(
        UserSettings.captureAdminMyProviderDataReviewQueueSettings,
        queueSettings
      );
  }
}